/**
 * G2eGente - Qualificação de Empregados
 * Análise de gap entre requisitos do cargo e qualificações do empregado
 */
import React, { useState, useEffect } from 'react';
import { employeesAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { 
  Users, 
  Search,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Clock,
  Loader2,
  Link2,
  FileText,
  GraduationCap,
  Briefcase,
  Download
} from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { QualificationGroupTable } from '../../components/trainings/QualificationGroupTable';
import { CompetencySection } from '../../components/trainings/CompetencySection';

const STATUS_CONFIG = {
  'atendido': { 
    icon: CheckCircle, 
    color: 'text-green-600', 
    bg: 'bg-green-50',
    badge: 'bg-green-100 text-green-700',
    label: 'Atendido' 
  },
  'vencendo': { 
    icon: Clock, 
    color: 'text-amber-600', 
    bg: 'bg-amber-50',
    badge: 'bg-amber-100 text-amber-700',
    label: 'Vencendo' 
  },
  'vencido': { 
    icon: AlertTriangle, 
    color: 'text-red-600', 
    bg: 'bg-red-50',
    badge: 'bg-red-100 text-red-700',
    label: 'Vencido' 
  },
  'nao_atendido': { 
    icon: XCircle, 
    color: 'text-slate-400', 
    bg: 'bg-slate-50',
    badge: 'bg-slate-100 text-slate-700',
    label: 'Pendente' 
  },
  'dispensado': { 
    icon: XCircle, 
    color: 'text-purple-500', 
    bg: 'bg-purple-50',
    badge: 'bg-purple-100 text-purple-700',
    label: 'Dispensado' 
  }
};

const TIPO_ICONS = {
  'escolaridade': GraduationCap,
  'treinamento': FileText,
  'requisito_legal': AlertTriangle,
  'conhecimento': Briefcase
};

// Cores leves para os títulos das seções por categoria
const CATEGORIA_COLORS = {
  'Escolaridade': 'bg-blue-100 text-blue-800 border-blue-200',
  'Treinamento Corporativo': 'bg-purple-100 text-purple-800 border-purple-200',
  'Treinamentos Obrigatórios': 'bg-purple-100 text-purple-800 border-purple-200',
  'Requisito Legal': 'bg-amber-100 text-amber-800 border-amber-200',
  'Requisitos Legais': 'bg-amber-100 text-amber-800 border-amber-200',
  'Conhecimento Específico': 'bg-teal-100 text-teal-800 border-teal-200',
  'Conhecimentos': 'bg-teal-100 text-teal-800 border-teal-200',
  'Habilidades': 'bg-cyan-100 text-cyan-800 border-cyan-200',
  'Competências': 'bg-indigo-100 text-indigo-800 border-indigo-200',
  'default': 'bg-slate-100 text-slate-800 border-slate-200'
};

export default function QualificacaoTab() {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [qualificationData, setQualificationData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [search, setSearch] = useState('');
  
  // Vinculação modal
  const [showVincularModal, setShowVincularModal] = useState(false);
  const [selectedRequisito, setSelectedRequisito] = useState(null);
  const [vinculacaoForm, setVinculacaoForm] = useState({
    fonte: 'curriculo',
    fonte_descricao: '',
    status: 'atendido',
    observacoes: ''
  });
  const [saving, setSaving] = useState(false);
  
  // Dispensar modal
  const [showDispensarModal, setShowDispensarModal] = useState(false);
  const [dispensarRequisito, setDispensarRequisito] = useState(null);
  const [dispensarMotivo, setDispensarMotivo] = useState('');
  const [savingDispensa, setSavingDispensa] = useState(false);

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const res = await employeesAPI.getAll({ situacao: 'ativo' });
      setEmployees(res.data || []);
    } catch (error) {
      toast.error('Erro ao carregar funcionários');
    } finally {
      setLoadingEmployees(false);
    }
  };

  const fetchQualificationRequirements = async (employeeId) => {
    setLoading(true);
    try {
      const res = await employeesAPI.getQualificationRequirements(employeeId);
      setQualificationData(res.data);
    } catch (error) {
      toast.error('Erro ao carregar requisitos de qualificação');
      setQualificationData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectEmployee = (emp) => {
    setSelectedEmployee(emp);
    fetchQualificationRequirements(emp.id);
  };

  const handleOpenVincular = (requisito) => {
    setSelectedRequisito(requisito);
    setVinculacaoForm({
      fonte: 'curriculo',
      fonte_descricao: '',
      status: 'atendido',
      observacoes: ''
    });
    setShowVincularModal(true);
  };

  const handleSaveVinculacao = async () => {
    if (!vinculacaoForm.fonte_descricao.trim()) {
      toast.error('Informe a descrição da comprovação');
      return;
    }
    
    setSaving(true);
    try {
      await employeesAPI.saveQualification(selectedEmployee.id, {
        requisito_tipo: selectedRequisito.tipo,
        requisito_valor: selectedRequisito.requisito,
        ...vinculacaoForm
      });
      toast.success('Vinculação salva com sucesso');
      setShowVincularModal(false);
      fetchQualificationRequirements(selectedEmployee.id);
    } catch (error) {
      toast.error('Erro ao salvar vinculação');
    } finally {
      setSaving(false);
    }
  };

  const [removeTarget, setRemoveTarget] = useState(null);

  const handleRemoveVinculacao = (requisito) => setRemoveTarget(requisito);

  const confirmRemoveVinculacao = async () => {
    const requisito = removeTarget;
    setRemoveTarget(null);
    try {
      await employeesAPI.deleteQualification(
        selectedEmployee.id, 
        requisito.tipo, 
        requisito.requisito
      );
      toast.success('Vinculação removida');
      fetchQualificationRequirements(selectedEmployee.id);
    } catch (error) {
      toast.error('Erro ao remover vinculação');
    }
  };

  const handleOpenDispensarModal = (requisito) => {
    setDispensarRequisito(requisito);
    setDispensarMotivo('');
    setShowDispensarModal(true);
  };

  const handleDispensarRequisito = async () => {
    if (!dispensarMotivo.trim()) {
      toast.error('Informe o motivo da dispensa');
      return;
    }
    
    setSavingDispensa(true);
    try {
      await employeesAPI.saveQualification(selectedEmployee.id, {
        requisito_tipo: dispensarRequisito.tipo,
        requisito_valor: dispensarRequisito.requisito,
        fonte: 'dispensa',
        fonte_descricao: dispensarMotivo,
        status: 'dispensado',
        observacoes: `Requisito dispensado pela empresa: ${dispensarMotivo}`
      });
      toast.success('Requisito dispensado');
      setShowDispensarModal(false);
      fetchQualificationRequirements(selectedEmployee.id);
    } catch (error) {
      toast.error('Erro ao dispensar requisito');
    } finally {
      setSavingDispensa(false);
    }
  };

  const filteredEmployees = employees.filter(emp =>
    emp.nome?.toLowerCase().includes(search.toLowerCase()) ||
    emp.cargo?.toLowerCase().includes(search.toLowerCase())
  );

  const groupReqs = (grupo) => (qualificationData?.requisitos || []).filter(r => r.grupo === grupo);

  const handleQuickMark = async (req, status) => {
    try {
      await employeesAPI.saveQualification(selectedEmployee.id, {
        requisito_tipo: req.tipo,
        requisito_valor: req.requisito,
        fonte: 'verificacao_manual',
        fonte_descricao: `${status === 'atendido' ? 'Atende' : 'Não atende'} — verificado em ${new Date().toLocaleDateString('pt-BR')}`,
        status
      });
      toast.success('Verificação registrada');
      fetchQualificationRequirements(selectedEmployee.id);
    } catch (error) {
      toast.error('Erro ao registrar verificação');
    }
  };

  const qualHandlers = {
    onVincular: handleOpenVincular,
    onRemove: handleRemoveVinculacao,
    onDispensar: handleOpenDispensarModal,
    onMark: handleQuickMark,
  };

  return (
    <div className="space-y-6">
      {/* Employee Selector */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="w-5 h-5 text-cyan-600" />
            Selecione o Empregado
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por nome ou cargo..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select 
              value={selectedEmployee?.id || ''} 
              onValueChange={(id) => {
                const emp = employees.find(e => e.id === id);
                if (emp) handleSelectEmployee(emp);
              }}
            >
              <SelectTrigger className="w-full md:w-[350px]">
                <SelectValue placeholder="Selecione um funcionário" />
              </SelectTrigger>
              <SelectContent>
                {loadingEmployees ? (
                  <div className="p-4 text-center">
                    <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                  </div>
                ) : (
                  filteredEmployees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.nome} - {emp.cargo}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
        </div>
      )}

      {/* No employee selected */}
      {!loading && !selectedEmployee && (
        <Card>
          <CardContent className="p-12 text-center">
            <Users className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-600">
              Selecione um empregado para visualizar sua qualificação
            </h3>
            <p className="text-slate-500 mt-2">
              O sistema comparará os requisitos do cargo com as qualificações do funcionário
            </p>
          </CardContent>
        </Card>
      )}

      {/* Qualification Data */}
      {!loading && qualificationData && (
        <>
          {/* Employee Header */}
          <Card className="bg-gradient-to-r from-cyan-50 to-blue-50 border-cyan-200">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">
                    {qualificationData.employee_nome}
                  </h2>
                  <p className="text-slate-600">{qualificationData.employee_cargo}</p>
                  {qualificationData.employee_data_admissao && (
                    <p className="text-sm text-slate-500 mt-1">
                      Admissão: {new Date(qualificationData.employee_data_admissao).toLocaleDateString('pt-BR')}
                    </p>
                  )}
                </div>
                
                {/* Summary */}
                <div className="flex gap-3">
                  <div className="text-center px-4 py-2 bg-green-100 rounded-lg">
                    <div className="text-2xl font-bold text-green-700">
                      {qualificationData.resumo?.atendidos || 0}
                    </div>
                    <div className="text-xs text-green-600">Atendidos</div>
                  </div>
                  <div className="text-center px-4 py-2 bg-amber-100 rounded-lg">
                    <div className="text-2xl font-bold text-amber-700">
                      {qualificationData.resumo?.vencendo || 0}
                    </div>
                    <div className="text-xs text-amber-600">Vencendo</div>
                  </div>
                  <div className="text-center px-4 py-2 bg-red-100 rounded-lg">
                    <div className="text-2xl font-bold text-red-700">
                      {(qualificationData.resumo?.vencidos || 0) + (qualificationData.resumo?.pendentes || 0)}
                    </div>
                    <div className="text-xs text-red-600">Pendentes</div>
                  </div>
                </div>
              </div>

              {!qualificationData.cargo_encontrado && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center gap-2 text-amber-700">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="text-sm font-medium">
                      Descrição do cargo &quot;{qualificationData.employee_cargo}&quot; não encontrada
                    </span>
                  </div>
                  <p className="text-sm text-amber-600 mt-1">
                    Cadastre a descrição do cargo em Engenharia de Cargo para definir os requisitos.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Currículo Section */}
          {(qualificationData.curriculo?.formacoes?.length > 0 || 
            qualificationData.curriculo?.cursos_complementares?.length > 0) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Currículo do Empregado</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {qualificationData.curriculo?.formacoes?.length > 0 && (
                  <div>
                    <h4 className="font-medium text-slate-700 mb-2">Formação Acadêmica</h4>
                    <div className="space-y-2">
                      {qualificationData.curriculo.formacoes.map((f, i) => (
                        <div key={i} className="p-3 bg-slate-50 rounded-lg">
                          <div className="font-medium">{f.curso}</div>
                          <div className="text-sm text-slate-600">
                            {f.instituicao} {f.ano_conclusao && `(${f.ano_conclusao})`}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {qualificationData.curriculo?.cursos_complementares?.length > 0 && (
                  <div>
                    <h4 className="font-medium text-slate-700 mb-2">Cursos Complementares</h4>
                    <div className="space-y-2">
                      {qualificationData.curriculo.cursos_complementares.map((c, i) => (
                        <div key={i} className="p-3 bg-slate-50 rounded-lg">
                          <div className="font-medium">{c.nome}</div>
                          <div className="text-sm text-slate-600">
                            {c.instituicao} {c.carga_horaria && `• ${c.carga_horaria}h`} {c.ano && `• ${c.ano}`}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Requisitos separados: Treinamentos / Requisitos do Cargo / Habilidades e Competências */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Requisitos do Cargo vs Qualificações</CardTitle>
              {qualificationData.cargo_encontrado && (
                <p className="text-sm text-slate-500" data-testid="qual-cargo-vinculo">
                  Descrição: <strong>{qualificationData.cargo_titulo}</strong>
                  {qualificationData.cargo_codigo ? ` (cód. ${qualificationData.cargo_codigo})` : ''} —{' '}
                  {qualificationData.cargo_vinculado_por === 'codigo' ? 'vinculado pelo código do cargo' : 'vinculado pelo nome do cargo'}
                </p>
              )}
              {qualificationData.cargo_vinculado_por === 'nome' && (
                <div className="mt-2 p-2 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-800" data-testid="qual-vinculo-nome-alert">
                  Vinculado pelo nome do cargo — cadastre o código do cargo no funcionário e na descrição de cargo para um vínculo exato.
                </div>
              )}
            </CardHeader>
            <CardContent className="space-y-5">
              <QualificationGroupTable grupo="treinamento" requisitos={groupReqs('treinamento')} handlers={qualHandlers} />
              <div>
                <QualificationGroupTable grupo="requisito" requisitos={groupReqs('requisito')} handlers={qualHandlers} />
                {qualificationData.resumo_requisitos && (
                  <p className="text-xs text-slate-500 mt-2" data-testid="qual-resumo-requisitos">
                    Atende: {qualificationData.resumo_requisitos.atende} · Não atende: {qualificationData.resumo_requisitos.nao_atende} ·
                    Não verificado: {qualificationData.resumo_requisitos.nao_verificado} · Dispensado: {qualificationData.resumo_requisitos.dispensado}
                  </p>
                )}
              </div>
              <CompetencySection employee={selectedEmployee} />
            </CardContent>
          </Card>

          {/* Treinamentos Realizados Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-emerald-600" />
                Treinamentos Realizados
              </CardTitle>
              {qualificationData.resumo_treinamentos && (
                <div className="flex gap-3 mt-2">
                  <Badge className="bg-emerald-100 text-emerald-700">
                    {qualificationData.resumo_treinamentos.validos || 0} válidos
                  </Badge>
                  <Badge className="bg-amber-100 text-amber-700">
                    {qualificationData.resumo_treinamentos.vencendo || 0} vencendo
                  </Badge>
                  <Badge className="bg-red-100 text-red-700">
                    {qualificationData.resumo_treinamentos.vencidos || 0} vencidos
                  </Badge>
                </div>
              )}
            </CardHeader>
            <CardContent>
              {(!qualificationData.treinamentos_realizados || qualificationData.treinamentos_realizados.length === 0) ? (
                <div className="text-center py-8 text-slate-500">
                  <GraduationCap className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  Nenhum treinamento registrado para este funcionário
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Treinamento</TableHead>
                      <TableHead>Categoria</TableHead>
                      <TableHead className="text-center">Carga Horária</TableHead>
                      <TableHead className="text-center">Data Realização</TableHead>
                      <TableHead className="text-center">Validade</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {qualificationData.treinamentos_realizados.map((treinamento) => {
                      const statusConfig = {
                        'valido': { bg: 'bg-green-100 text-green-700', label: 'Válido' },
                        'vencendo': { bg: 'bg-amber-100 text-amber-700', label: 'Vencendo' },
                        'vencido': { bg: 'bg-red-100 text-red-700', label: 'Vencido' }
                      }[treinamento.status] || { bg: 'bg-slate-100 text-slate-700', label: treinamento.status };
                      
                      return (
                        <TableRow key={treinamento.id}>
                          <TableCell className="font-medium">
                            {treinamento.nome}
                          </TableCell>
                          <TableCell className="text-slate-600">
                            {treinamento.categoria || '-'}
                          </TableCell>
                          <TableCell className="text-center">
                            {treinamento.carga_horaria ? `${treinamento.carga_horaria}h` : '-'}
                          </TableCell>
                          <TableCell className="text-center">
                            {treinamento.data_realizacao 
                              ? new Date(treinamento.data_realizacao).toLocaleDateString('pt-BR')
                              : '-'}
                          </TableCell>
                          <TableCell className="text-center">
                            {treinamento.data_validade 
                              ? new Date(treinamento.data_validade).toLocaleDateString('pt-BR')
                              : 'Sem validade'}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge className={statusConfig.bg}>
                              {statusConfig.label}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Vinculação Modal */}
      <Dialog open={showVincularModal} onOpenChange={setShowVincularModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-cyan-600" />
              Vincular Comprovação
            </DialogTitle>
            <DialogDescription>
              Requisito: <strong>{selectedRequisito?.requisito}</strong>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Currículo disponível */}
            {(qualificationData?.curriculo?.formacoes?.length > 0 || 
              qualificationData?.curriculo?.cursos_complementares?.length > 0) && (
              <div className="p-4 bg-slate-50 rounded-lg">
                <h4 className="font-medium text-slate-700 mb-2">Currículo disponível:</h4>
                <div className="space-y-1 text-sm max-h-40 overflow-y-auto">
                  {qualificationData?.curriculo?.formacoes?.map((f, i) => (
                    <button
                      key={`form-${i}`}
                      type="button"
                      onClick={() => setVinculacaoForm(prev => ({
                        ...prev,
                        fonte_descricao: `${f.curso} - ${f.instituicao} (${f.ano_conclusao || 'em andamento'})`
                      }))}
                      className="block w-full text-left p-2 hover:bg-slate-100 rounded"
                    >
                      • {f.curso} - {f.instituicao}
                    </button>
                  ))}
                  {qualificationData?.curriculo?.cursos_complementares?.map((c, i) => (
                    <button
                      key={`curso-${i}`}
                      type="button"
                      onClick={() => setVinculacaoForm(prev => ({
                        ...prev,
                        fonte_descricao: `${c.nome} - ${c.instituicao} ${c.carga_horaria ? `(${c.carga_horaria}h)` : ''}`
                      }))}
                      className="block w-full text-left p-2 hover:bg-slate-100 rounded"
                    >
                      • {c.nome} - {c.instituicao}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <Label>Fonte da Comprovação</Label>
              <Select
                value={vinculacaoForm.fonte}
                onValueChange={(v) => setVinculacaoForm(prev => ({ ...prev, fonte: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="curriculo">Currículo do Empregado</SelectItem>
                  <SelectItem value="documento">Documento Apresentado</SelectItem>
                  <SelectItem value="declaracao">Declaração</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Descrição da Comprovação *</Label>
              <Textarea
                value={vinculacaoForm.fonte_descricao}
                onChange={(e) => setVinculacaoForm(prev => ({ 
                  ...prev, 
                  fonte_descricao: e.target.value 
                }))}
                placeholder="Ex: Curso de Excel Avançado - Senac (40h) - 2023"
                rows={3}
              />
            </div>

            <div>
              <Label>Status</Label>
              <Select
                value={vinculacaoForm.status}
                onValueChange={(v) => setVinculacaoForm(prev => ({ ...prev, status: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="atendido">Atendido</SelectItem>
                  <SelectItem value="parcial">Parcialmente Atendido</SelectItem>
                  <SelectItem value="nao_atendido">Não Atendido</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Observações</Label>
              <Textarea
                value={vinculacaoForm.observacoes}
                onChange={(e) => setVinculacaoForm(prev => ({ 
                  ...prev, 
                  observacoes: e.target.value 
                }))}
                placeholder="Observações adicionais..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVincularModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSaveVinculacao}
              disabled={saving || !vinculacaoForm.fonte_descricao.trim()}
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Salvar Vinculação
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Dispensar Requisito */}
      <Dialog open={showDispensarModal} onOpenChange={setShowDispensarModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-700">
              <XCircle className="w-5 h-5" />
              Dispensar Requisito
            </DialogTitle>
            <DialogDescription>
              Requisito: <strong>{dispensarRequisito?.requisito}</strong>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
              <p className="text-sm text-purple-700">
                Ao dispensar este requisito, ele não será mais contabilizado na análise de qualificação deste empregado.
                Essa ação pode ser revertida posteriormente.
              </p>
            </div>

            <div>
              <Label>Motivo da Dispensa *</Label>
              <Textarea
                value={dispensarMotivo}
                onChange={(e) => setDispensarMotivo(e.target.value)}
                placeholder="Ex: Função não requer este conhecimento específico"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDispensarModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleDispensarRequisito}
              disabled={savingDispensa || !dispensarMotivo.trim()}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {savingDispensa && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Confirmar Dispensa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AlertDialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
        <AlertDialogContent data-testid="qual-remove-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remover vinculação?</AlertDialogTitle>
            <AlertDialogDescription>
              {removeTarget?.requisito} voltará ao status inicial.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="qual-remove-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRemoveVinculacao} className="bg-red-600 hover:bg-red-700" data-testid="qual-remove-confirm">
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
